import { useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GripVertical, Layers, Plus, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const PRIORITIES = ["low", "medium", "high", "critical"];

export default function ModulePlanning() {
  const { project } = useOutletContext();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useApp();
  const [params] = useSearchParams();
  const epicId = params.get("epic");
  const [adding, setAdding] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", estimated_hours: "", priority: "medium", blocked_by: [], source_requirement_id: "" });

  const { data: epic } = useQuery({ queryKey: ["item", epicId], queryFn: () => get(`/pm/items/${epicId}`), enabled: !!epicId });
  const { data: allItems = [], isLoading } = useQuery({ queryKey: ["items", project.id], queryFn: () => get(`/pm/projects/${project.id}/items`) });
  const { data: coverage = {} } = useQuery({ queryKey: ["test-coverage", project.id], queryFn: () => get(`/pm/projects/${project.id}/test-coverage`) });
  const { data: client } = useQuery({ queryKey: ["client", project.client_id], queryFn: () => get(`/crm/clients/${project.client_id}`), enabled: !!project.client_id });
  const { data: requirements = [] } = useQuery({
    queryKey: ["lead-requirements", client?.source_lead_id],
    queryFn: () => get(`/crm/leads/${client.source_lead_id}/requirements`),
    enabled: !!client?.source_lead_id,
  });
  const modules = allItems.filter((i) => i.parent_id === epicId && i.type === "Module").sort((a, b) => (a.order_index || 0) - (b.order_index || 0));

  const create = useMutation({
    mutationFn: (body) => post(`/pm/projects/${project.id}/items`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["items", project.id] }); toast.success("Module created"); setAdding(false); setForm({ title: "", description: "", estimated_hours: "", priority: "medium", blocked_by: [], source_requirement_id: "" }); },
  });
  const reorder = useMutation({
    mutationFn: (order) => post(`/pm/projects/${project.id}/items/reorder`, { order }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["items", project.id] }),
  });
  const moveToBoard = useMutation({
    mutationFn: (wid) => post(`/pm/items/${wid}/move-to-board`, { cascade: true }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["items", project.id] }); toast.success("Moved to board"); },
  });

  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const ids = modules.map((m) => m.id);
    const from = ids.indexOf(dragId), to = ids.indexOf(targetId);
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    reorder.mutate(ids);
    setDragId(null);
  };

  return (
    <div>
      <div className="mb-2 text-xs text-muted-foreground">
        <button onClick={() => navigate(`/pm/projects/${project.id}/planning/epics`)} className="hover:underline">Epic Planning</button>
        {epic && <> / {epic.title} / Modules</>}
      </div>
      <PageHeader title="Module Planning" subtitle="Functional groupings within this epic.">
        {can("pm", "create") && epicId && (
          <Button data-testid="add-module-btn" onClick={() => setAdding((s) => !s)}><Plus className="mr-1.5 h-4 w-4" />New Module</Button>
        )}
      </PageHeader>

      {!epicId ? (
        <EmptyState icon={Layers} title="No epic selected" description="Open a module list from Epic Planning." />
      ) : isLoading ? <Skeleton className="h-64" /> : (
        <Section>
          {adding && (
            <div className="border-b border-border/60 bg-muted/30 p-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Title *</Label><Input data-testid="module-title" className="mt-1" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></div>
                <div className="col-span-2"><Label>Description</Label><Textarea data-testid="module-description" className="mt-1" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
                <div><Label>Estimated Hours</Label><Input data-testid="module-hours" type="number" className="mt-1" value={form.estimated_hours} onChange={(e) => setForm((f) => ({ ...f, estimated_hours: e.target.value }))} /></div>
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                    <SelectTrigger className="mt-1" data-testid="module-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Dependencies (other modules in this epic)</Label>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {modules.map((m) => (
                      <button key={m.id} type="button"
                        onClick={() => setForm((f) => ({ ...f, blocked_by: f.blocked_by.includes(m.id) ? f.blocked_by.filter((x) => x !== m.id) : [...f.blocked_by, m.id] }))}
                        className={`rounded-full border px-2.5 py-1 text-xs ${form.blocked_by.includes(m.id) ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
                        {m.title}
                      </button>
                    ))}
                    {modules.length === 0 && <span className="text-xs text-muted-foreground">No other modules yet.</span>}
                  </div>
                </div>
                {requirements.length > 0 && (
                  <div className="col-span-2">
                    <Label>Link to CRM Requirement (optional)</Label>
                    <Select value={form.source_requirement_id} onValueChange={(v) => setForm((f) => ({ ...f, source_requirement_id: v }))}>
                      <SelectTrigger className="mt-1" data-testid="module-requirement"><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent>{requirements.map((r) => <SelectItem key={r.id} value={r.id}>{r.description}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
                <Button size="sm" data-testid="module-submit" onClick={() => {
                  if (!form.title) { toast.error("Title is required"); return; }
                  create.mutate({ ...form, type: "Module", parent_id: epicId, in_planning: true, estimated_hours: Number(form.estimated_hours) || 0, source_requirement_id: form.source_requirement_id || null });
                }}>Create</Button>
              </div>
            </div>
          )}
          {!modules.length ? <EmptyState icon={Layers} title="No modules yet" /> : (
            <div className="divide-y divide-border">
              {modules.map((m) => (
                <div key={m.id} draggable={can("pm", "edit")} onDragStart={() => setDragId(m.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(m.id)}
                  className="flex items-center gap-3 px-4 py-3 text-sm">
                  {can("pm", "edit") && <GripVertical className="h-4 w-4 cursor-grab text-muted-foreground" />}
                  <button className="flex-1 text-left font-medium hover:underline" onClick={() => navigate(`/pm/projects/${project.id}/items/${m.id}`)}>{m.title}</button>
                  <span className="w-16 text-right text-xs text-muted-foreground">{m.estimated_hours || 0}h</span>
                  <StatusBadge status={m.priority} />
                  <div className="flex gap-1">
                    {(m.blocked_by || []).map((bid) => {
                      const dep = allItems.find((i) => i.id === bid);
                      return dep ? <span key={bid} className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{dep.title}</span> : null;
                    })}
                  </div>
                  <StatusBadge status={m.column_id?.includes("col-done") ? "done" : "active"} />
                  {coverage[m.id] && <span className="text-xs text-muted-foreground">{coverage[m.id].passing}/{coverage[m.id].total} passing</span>}
                  <Button size="sm" variant="ghost" onClick={() => navigate(`/pm/projects/${project.id}/planning/tasks?module=${m.id}`)}>
                    Tasks <ArrowRight className="ml-1 h-3.5 w-3.5" />
                  </Button>
                  {m.in_planning && can("pm", "edit") && (
                    <Button size="sm" variant="outline" data-testid={`move-to-board-${m.id}`} onClick={() => moveToBoard.mutate(m.id)}>Move to Board</Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>
      )}
    </div>
  );
}
